import CollectionControl from '@/components/collection-control';
import DateInput from "@/components/date-input";
import { DynPanelAware } from '@/components/dyn-panel';
import LabelWithHelp from "@/components/label-with-help";
import ListDropdown from '@/components/list-dropdown';
import { CommitableDataSlidePanel } from "@/components/slide-panel";
import XInput from '@/components/x-input';
import XTable from '@/components/x-table';
import ActionTd from '@/components/x-table/action-td';
import ActionTH from '@/components/x-table/action-th';
import XTh from '@/components/x-table/x-th';
import { $t } from "@/i18n";
import { ellipsis, formatDate, lexLabel, notEmpty, strEqNoCase, loadingClass } from '@/lib/helpers';
import RightsAware from '@/lib/mixins/rights-aware';
import BeanCaviteAssociee from '@/lib/models/bean-cavite-associee';
import BeanCoord from '@/lib/models/bean-coord';
import CavCavite from '@/lib/models/cav-cavite';
import User, { FLEX_PROFILES } from '@/lib/models/user';
import { checkBss, checkDistanceWithOtherCavites, loadMouvementsByCenter, getCaviteByNum, saveCavite, deleteCavite } from '@/lib/services';
import PanelCom from '@/modules/cavite-edit/components/panel-com';
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Action, Getter, Mutation, State } from 'vuex-class';
import PanelCavAssoc from '../components/panel-cav-assoc';
import PanelExportPdf from '../components/panel-export-pdf';
import Icon from '@/components/icon';
import { LsitCavConfidentialite, LcavDangerosite, LexCodeFiabilite } from '@/lib/models/gen-lexs';

const COM_DIST_THRESHOLD = 10000;

@Component
export default class CaviteEdit extends Mixins<CommitableDataSlidePanel<CavCavite>, RightsAware, DynPanelAware>(CommitableDataSlidePanel, RightsAware, DynPanelAware) {

  @Action notify;
  @Action refreshLastFetched;

  @Mutation('toggleHelp') toggleHelp;
  @Mutation('setEditedCavite') storeSetEditedCavite;

  @Getter getLexique;
  @Getter getPositionableBeanCoord;
  @Getter userhHasHigherFlexProfileThan;
  @Getter userhHasMinimumFlexProfileThan;
  @Getter loggedUser: User;

  @State(s => s.caviteEdit.editedCaviteGeometrieAction) geometrieAction: {what: string; file?: string};

  @Prop({required: true, type: [Number, String]}) readonly id!: number | string;

  protected bssOk: boolean = false;
  protected loading = {
    checkBss: 0,
    exportPdf: 0,
    saving: 0,
    deleting: 0
  };
  protected hasBeenEdited: boolean = false;
  protected editionWatcher: any = null;

  get mode() {
    return this.id === "new" ? "create" : "edit";
  }

  async loadEditedData() {
    const d = await getCaviteByNum(`${this.id}`);
    return d;
  }

  async created() {
    this.mode === "create" && (this.data = new CavCavite());
    this.validators = [
      {fn: d => !((notEmpty(d.indiceBss) || notEmpty(d.designationBss)) && !this.bssOk), message: "Le code BSS invalide ou non vérifié"},
      {fn: d => notEmpty(d.commune), message: "La commune est manquante"},
      {fn: d => notEmpty(d.nomCavite), message: "Le nom de la cavité est manquant"},
      {fn: d => notEmpty(d.typeCavite), message: "Le type de cavité est manquant"},
      {fn: d => notEmpty(d.natureCavite), message: "La nature de la cavité est manquante"},
      {fn: d => notEmpty(d.statut), message: "Le statut de la cavité est manquant"},
      {fn: d => notEmpty(d.reperageGeographique), message: "Le type de repérage géographique est manquant"},
      {fn: d => notEmpty(d.positionnement), message: "Le positionnement est manquant"},
      {fn: d => notEmpty(d.sourceCoordonnees), message: "Le champ « Source coordonnées » est manquant"},
      {fn: d => notEmpty(d.precisionXY), message: "Le champ « Précision XY » est manquant"},
      {fn: d => notEmpty(d.confidentialite), message: "Le champ « Confidentialité » est manquant"},
      {fn: d => notEmpty(d.dateValidite), message: "Le champ « Date de validité » est manquant ou incorrect"},
      {fn: d => notEmpty(d.cavSourcesInfos), message: "Au moins une source d'information est requise"},
      {
        fn: _ => this.currentCavBeanCoord != null,
        message: "Les coordonnées de la cavité sont requises"
      },
      {
        enabled: d => Boolean(d.ddtm59Links?.length),
        fn: d => d.ddtm59Links?.every(r => notEmpty(r.link)),
        message: "Un lien avec la base DDTM59 est incomplet"
      },
      {
        enabled: _ => this.currentCavBeanCoord != null,
        fn: d => !(d.cavEvtsSurface.length === 0 || d.cavEvtsSurface.some(es => notEmpty(es.idBdmvt))),
        confirm: this.checkAndConfirmMvtTerrain
      },
      {
        enabled: _ => this.currentCavBeanCoord != null,
        fn: _ => false,
        confirm: this.checkAndConfirmOtherCavDistance
      },
      {
        enabled: d => this.currentCavBeanCoord != null && d.commune != null,
        fn: _ => false,
        confirm: this.checkAndConfirmComDistance
      }
    ];
  }

  @Watch("data")
  onDataChange() {
    this.storeSetEditedCavite(this.data);
    this.readonly_ = !(this.data.numCavite == null || this.userIsOwner || this.userhHasHigherFlexProfileThan(FLEX_PROFILES.AGENT_SAISIE));
    this.editionWatcher && this.editionWatcher();
    this.$nextTick(() => {
      this.editionWatcher = this.$watch("data", this.onDataDeepChange, {deep: true})
    });
  }

  onDataDeepChange() {
    this.hasBeenEdited = true;
  }

  /* parent overrides */

  get rootClasses() {
    return ["is-fullwidth"];
  }

  get headClasses() {
    return ["justify-space-between"];
  }

  /* ---------------- */

  get currentCavBeanCoord() {
    return this.getPositionableBeanCoord(this.data);
  }

  get userIsOwner() {
    return this.data.cuserSaisie === this.loggedUser.mail;
  }

  onToggleHelp() {
    this.toggleHelp();
  }

  onClickClose() {
    this.$router.push({name: "index"});
  }

  async onClickDelete() {
    const confirmed = await this.promiseConfirmPanel("Êtes-vous sûr de vouloir supprimer cette cavité ? Cette opération est irréversible.");
    if (confirmed) {
      this.loading.deleting++;
      await deleteCavite({numCavite: this.data.numCavite});
      await this.refreshLastFetched();
      this.loading.deleting--;
      this.notify({
        message: <span>La cavité a bien été supprimée</span>,
        level: "success"
      });
      this.$router.push({name: "index"});
    }
  }

  onClickCancel() {
    this.$router.push({name: "index"});
  }

  async onClickExportPdf() {
    this.pushPanel({
      clazz: PanelExportPdf,
      props: {cavite: this.data},
      on: {
        commit: async ({emitter}) => {
          emitter.remove()
        }
      }
    });
  }

  async onClickCheckBss() {
    this.loading.checkBss++;
    const bean = await checkBss({designationBss: this.data.designationBss, numBss: this.data.indiceBss});
    if (!bean) {
      this.notify({
        message: <span>L'indice BSS spécifié ne semble pas éxister ou la désignation ne correspond pas.</span>,
        level: "danger"
      });
      this.bssOk = false;
    } else {
      const incomingProj = this.getLexique('ComSystProjectFrance').find(l => strEqNoCase(l.epsg, bean.epsg));
      const beanL93 = bean.asL93();
      this.data.inject({
        xouvrage: bean.x,
        youvrage: bean.y,
        xouv93: beanL93.x,
        youv93: beanL93.y,
        lambertOuvrage: incomingProj.codeSystProjection
      });
      this.notify({
        message: <span>Les coordonnées ont été importées de la base BSS</span>,
        level: "success"
      });
      this.bssOk = true;
    }
    this.loading.checkBss--;
  }

  async onClickCommit() {
    this.loading.saving++;
    if (await this.doValidate()) {
      this.data.inject({
        cuserModif: this.loggedUser.mail,
        dateMajCavite: new Date()
      });

      if (!this.data.numCavite) {
        this.data.inject({
          cuserSaisie: this.loggedUser.mail
        });
      }

      if (this.loggedUser.hasFlexProfile(FLEX_PROFILES.AGENT_SAISIE)) {
        this.data.statutSaisie = 1;
        this.data.statutDiffusion = 1;
      }

      this.data = await saveCavite({
        cavite: this.data as CavCavite,
        geometrieAction: this.geometrieAction
      });
      this.$router.push({name: "cavite.edit", params: {id: this.data.numCavite}});
      this.notify({
        message: <span>La cavité a été enregistrée</span>,
        level: "success"
      });
      await this.refreshLastFetched();
      this.editionWatcher && this.editionWatcher(); //unwatch
      this.hasBeenEdited = false;
    }
    this.loading.saving--;
  }

  async checkAndConfirmMvtTerrain() {
    const coords = this.currentCavBeanCoord;
    const mvtInfos = await loadMouvementsByCenter({coords, distance: (this.data.precisionXY || 0) * 2});
    if (mvtInfos && mvtInfos.length) {
      return this.promiseConfirmPanel(
        <span><b>Attention, la cavité en cours de saisie se trouve à proximité des mouvements de terrain suivants (BDMVT) :</b>
          <ul class="with-grey-bullets">
            {mvtInfos.map(({userInfo}) => <li>{userInfo}</li>)}
          </ul>
        Voulez-vous continuer l'enregistrement ?
        </span>
      );
    }
    return true;
  }

  async checkAndConfirmOtherCavDistance() {
    const coords = this.currentCavBeanCoord;
    const beanQr = await checkDistanceWithOtherCavites({
      numCavite: this.data.numCavite,
      coords,
      distance: 20 + (this.data.precisionXY || 0)
    });
    if (beanQr.cavites.length) {
      return this.promiseConfirmPanel(
        <span><b>Attention, des cavités existent à moins de 20m : </b>
          <ul class="with-grey-bullets">
            {beanQr.cavites.map(({userInfo}) => <li>{userInfo}</li>)}
          </ul>
          Voulez-vous continuer l'enregistrement ?
        </span>
      );
    }
    return true;
  }

  async checkAndConfirmComDistance() {
    const comCoords = new BeanCoord().inject({
     x:  this.data.commune.longi,
     y: this.data.commune.lat,
     epsg: "epsg:4326"
    });
    if (this.currentCavBeanCoord.getL93DistanceTo(comCoords) > COM_DIST_THRESHOLD) {
      return this.promiseConfirmPanel("La cavité est située à plus de 10 km du centroïde de la commune, voulez-vous continuer ?");
    }
    return true;
  }

  mounted() { }

  renderHead() {
    return [
      <span>{this.mode === "create" ? "Ajout" : "Edition"} d'une cavité</span>,
      <div class="is-flex justify-flex-end cav-edit-head-buttons is-airy">
        {this.data && [
        this.data.numCavite ? (
          <button class={`button is-small ${loadingClass(this.loading.exportPdf)}`} onClick={this.onClickExportPdf}>
            <Icon icon="file-pdf" class="icon is-medium"/>
            <span>Export PDF</span>
          </button>
        ) : null,
        <button class="button is-small" onClick={this.onToggleHelp}>
          <Icon icon="question-circle" class="icon is-medium info"/>
          <span>Aide</span>
        </button>
        ].filter(Boolean)}
        <button class="button is-small" onClick={this.onClickClose}>
          <Icon icon="window-close" class="icon is-medium grey"/>
          <span>Fermer la fiche</span>
        </button>
      </div>
    ];
  }

  renderBody() {
    if (!this.data) return <div class="async is-loading is-large" style="height: 50vh"></div>;
    return (
      <div style="height: 80vh">
        <div class="columns">
          <div class="column is-three-quarters is-flex flex-column">
            <div class="block-ident panel">
              <div class="panel-block has-background-light">
                <div class="is-fluid">
                  <div class="is-flex is-airy">
                    <div class="field is-fluid">
                      <label class="label is-small">Identifiant</label>
                      <p class="control">
                        <XInput value={this.data.numCavite} class="input is-small" placeholder="Auto-généré" readonly={true}/>
                      </p>
                    </div>
                    <div class="field is-fluid">
                      <label class="label is-small">Commune</label>
                      <div class="field has-addons">
                        <p class="control">
                          <XInput value={this.data.commune && this.data.commune.nom} class="input is-small" readonly={true}/>
                        </p>
                        {!this.isReadonly && (
                        <p class="control">
                          <button class="button is-small"
                            onClick={() => this.pushPanel({
                              clazz: PanelCom,
                              props: {},
                              on: {
                                commit: ({emitter, data: com}) => (this.data.commune = com, emitter.remove())
                              }
                            })}>
                            Choisir
                          </button>
                        </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div class="is-flex is-airy">
                    <div class="field is-fluid">
                      <label class="label is-small">Nom de la cavité (*)</label>
                      <p class="control">
                        <XInput vModel={this.data.nomCavite} class="input is-small" maxLength={100}/>
                      </p>
                    </div>
                    <div class="field is-fluid">
                      <label class="label is-small">Indice BSS</label>
                      <p class="control">
                        <XInput vModel={this.data.indiceBss} maxLength={10} onKeyup={() => this.bssOk = false} class="input is-small"/>
                      </p>
                    </div>
                    <div class="field is-fluid">
                      <label class="label is-small">Désign. BSS</label>
                      <p class="control is-flex is-airy">
                        <XInput vModel={this.data.designationBss} maxLength={6} onKeyup={() => this.bssOk = false}  class="input is-small"/>
                        {!this.isReadonly && (
                          <button class={`button is-small ${loadingClass(this.loading.checkBss)}`}
                            disabled={!(this.data.indiceBss && this.data.designationBss)}
                            onClick={this.onClickCheckBss}>Vérifier</button>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="block-main panel is-flex flex-column is-fluid">
              <div class="tabs">
                <ul>
                  <router-link tag="li" to={{name: 'cavite.edit.description'}}><a>Description</a></router-link>
                  <router-link tag="li" to={{name: 'cavite.edit.sourceInfos'}}><a>Sources d'information</a></router-link>
                  <router-link tag="li" to={{name: 'cavite.edit.descCompl'}}><a>Description complémentaire</a></router-link>
                  <router-link tag="li" to={{name: 'cavite.edit.commentaire'}}><a>Commentaire</a></router-link>
                  {this.data.isInPoc && this.loggedUser.hasPocAcces && (
                    <router-link tag="li" to={{name: 'cavite.edit.poc'}}><a>POC</a></router-link>
                  )}
                  {this.data.isInEms && this.loggedUser.hasEmsAcces && (
                    <router-link tag="li" to={{name: 'cavite.edit.ems'}}><a>EMS</a></router-link>
                  )}
                  {this.data.isInDdtm59 && this.loggedUser.hasDdtm59Acces && (
                    <router-link tag="li" to={{name: 'cavite.edit.ddtm59'}}><a>DDTM59</a></router-link>
                  )}
                </ul>
              </div>
              <div class="block-main-content">
                <router-view name="mainTab"/>
                <div class="required-fields-msg"><i>(*) champs obligatoires</i></div>
              </div>
            </div>
          </div>
          <div class="column main-right-column">
            <div class="panel">
              <div class="panel-block">
                <div class="is-fluid">
                  <div class="field">
                    <label class="label is-small">Date de validité (*)</label>
                    <p class="control">
                      <DateInput vModel={this.data.dateValidite}/>
                    </p>
                  </div>
                  <p class="is-size-7 has-text-grey-light">
                    <small>Il s'agit de la date la plus récente faisant référence à la cavité : découverte, document, visite de terrain ...&#xd; (jj/mm/aaaa)</small>
                  </p>
                </div>
              </div>
              {this.data.numCavite && (
              <div class="panel-block">
                <div class="columns is-fluid is-size-7">
                  <div class="column">
                    <p>Auteur : {this.data.cuserSaisie}</p>
                    <p>Maj : {formatDate(this.data.dateMajCavite)}</p>
                  </div>
                </div>
              </div>
              )}
            </div>
            <div class="panel">
              <div class="panel-block">
                <div class="is-fluid is-max100">
                  <div class="field">
                    <label class="label is-small">Niveau de confidentialité (*)</label>
                    <p class="control">
                      <ListDropdown class="basic"
                        vModel={this.data.confidentialite}
                        data={this.getLexique(LsitCavConfidentialite)}
                        labelFn={v => v.confidentialite}
                        transform={v => v.confidentialite}
                        prompt="-"/>
                    </p>
                  </div>
                  <div class="field">
                    <LabelWithHelp class="is-small has-text-weight-normal" withIcon={true}
                      message={$t("help.label.confidentialite.raison")}>
                        Préciser la raison
                    </LabelWithHelp>
                    <p class="control is-flex">
                      <XInput vModel={this.data.raisonConfidentialite} class="input is-small is-fluid" maxLength={255}/>
                    </p>
                  </div>
                  <div class="field">
                    <label class="label is-small has-text-weight-normal">Organisme demandeur</label>
                    <p class="control">
                      <XInput vModel={this.data.organismeChxConfidentialite} class="input is-small" maxLength={255}/>
                    </p>
                  </div>
                  <div class="field">
                    <label class="label is-small has-text-weight-normal">Dangerosité</label>
                    <p class="control">
                      <ListDropdown class="basic"
                        vModel={this.data.dangerosite}
                        data={this.getLexique(LcavDangerosite)}
                        labelFn={lexLabel}
                        transform={v => v.id}
                        prompt="-"/>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="panel">
              <div class="panel-block">
                <div class="is-fluid">
                  <div class="field is-fluid">
                    <LabelWithHelp class="is-small" withIcon={true}
                      message={$t("help.label.cavitesAssociees")}>
                        Cavités associées
                    </LabelWithHelp>
                    <CollectionControl
                      data={this.data.caviteAssocieeList}
                      clazz={PanelCavAssoc}
                      scopedSlots={{
                        body: ({ data, onEdit, onDelete }) => (
                          <XTable data={data}
                            class="is-full-width is-bordered is-small"
                            scopedSlots={{
                              header: () => (
                                <tr>
                                  <XTh prop="numCavite">ID</XTh>
                                  <XTh prop="nomCavite">Nom</XTh>
                                  <ActionTH/>
                                </tr>
                              ),
                              row: ({d}: {d: BeanCaviteAssociee}) => (
                                <tr>
                                  <td>{d.numCavite}</td>
                                  <td>{ellipsis(d.nomCavite, 100)}</td>
                                  <ActionTd
                                    withEdit={false}
                                    onEdit={() => onEdit(d)}
                                    onDelete={() => onDelete(d)}
                                  />
                                </tr>
                              )
                            }}>
                          </XTable>
                        )
                      }}/>
                  </div>
                  <div class="is-size-7">
                    <div>
                      <small><i>Si vous souhaitez faire part de remarque concernant une ou plusieurs associations, merci d'utiliser l'espace commentaire</i></small>
                    </div>
                    <a onClick={() => this.$router.push({name: "cavite.edit.commentaire"})} class="is-small">Ajouter un commentaire</a>
                  </div>
                </div>
              </div>
            </div>
            {this.data.isInDdtm59 && this.loggedUser.hasDdtm59Acces && (
            <div class="panel">
              <div class="panel-block">
                <div class="is-fluid">
                  <div class="field">
                    <label class="label is-small">Code de fiabilité</label>
                    <p class="control">
                      <ListDropdown class="basic"
                        vModel={this.data.lexCodeFiabilite}
                        data={this.getLexique(LexCodeFiabilite)}
                        labelFn={v => v.label}
                        transform={v => v.id}
                        prompt="Aucun"/>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderFooter() {
    if (!this.data) return null;
    return (
      <div class="columns is-fluid">
        <div class="column is-5">
          <div class="has-text-centered is-full-width has-background-grey-light has-text-white">Statuts de la cavité</div>
          <div class="columns">
            <div class="column is-half">
              <div class="field">
                <label class="label is-small">Validation</label>
                <ListDropdown class="basic" placement="top-start"
                  vModel={this.data.statutSaisie}
                  data={this.getLexique("StatutValidation")}
                  labelFn={lexLabel}
                  transform={v => v.id}
                  nullable={false}
                  prompt="-"/>
              </div>
            </div>
            <div class="column is-half">
              <div class="field">
                <label class="label is-small">Diffusion</label>
                <ListDropdown class="basic" placement="top-start"
                  vModel={this.data.statutDiffusion}
                  data={this.getLexique("StatutValidation")}
                  labelFn={lexLabel}
                  transform={v => v.id}
                  nullable={false}
                  readonly={!this.userhHasMinimumFlexProfileThan(FLEX_PROFILES.REF_THEM)}
                  prompt="-"/>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-5">
          {!this.isReadonly && (
          <button class={`button is-primary is-full-width is-full-height ${loadingClass(this.loading.saving)}`}
            disabled={Boolean(this.loading.deleting)}
            onClick={this.onClickCommit}>
            <Icon icon="save" class="icon is-large white"/>
            <span>Enregistrer la fiche</span>
          </button>
          )}
        </div>
        <div class="column is-2 is-flex flex-column is-airy-v">
          {!this.isReadonly && (
            <button class={`button is-small is-full-width is-fluid is-danger ${loadingClass(this.loading.deleting)}`}
              disabled={Boolean(this.loading.saving) || this.mode === "create"}
              onClick={this.onClickDelete}>Supprimer cette cavité</button>
          )}
          <button class="button is-small is-full-width is-fluid" onClick={this.onClickCancel}>{this.isReadonly || !this.hasBeenEdited ? 'Fermer' : 'Annuler'}</button>
        </div>
      </div>
    )
  }

}

// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-2ec7529a/index.tsx" });